import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { META, getGoal, setGoal, exportData, importData, resetProgress, getNewlyEarnedBadges } from "../lib/store";
import { Settings, Download, Upload, Trash2, Target, Minus, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BadgesGrid } from "../components/Badges";

export default function Reglages() {
  const { level } = useApp();
  const { user } = useAuth();
  const [goal, setGoalState] = useState(() => getGoal());
  const fileRef = useRef(null);

  useEffect(() => {
    getNewlyEarnedBadges(level).forEach((b) => toast.success(`Badge débloqué : ${b.label} !`, { description: b.desc }));
  }, [level]);

  const changeGoal = (delta) => {
    const v = Math.max(5, Math.min(200, goal + delta));
    setGoalState(v);
    setGoal(v);
  };

  const doExport = () => {
    const blob = new Blob([exportData()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hamprep-progression-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Progression exportée");
  };

  const doImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        importData(String(reader.result));
        toast.success("Progression importée");
        setTimeout(() => window.location.reload(), 600);
      } catch {
        toast.error("Fichier invalide");
      }
    };
    reader.readAsText(file);
  };

  const doReset = () => {
    if (window.confirm(`Réinitialiser toute la progression du niveau ${META[level].label} ? Cette action est irréversible.`)) {
      resetProgress(level);
      toast.success("Progression réinitialisée");
      setTimeout(() => window.location.reload(), 600);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary">
          <Settings size={14} /> Préférences
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Réglages</h1>
      </div>

      <BadgesGrid level={level} />

      <section className="rounded-sm border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Target size={18} className="text-primary" />
          <h2 className="font-display font-bold text-lg">Objectif quotidien</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Nombre de questions à répondre chaque jour.</p>
        <div className="flex items-center gap-2 mb-4">
          {[10, 20, 30].map((v) => (
            <button
              key={v}
              onClick={() => { setGoalState(v); setGoal(v); }}
              data-testid={`goal-preset-${v}`}
              className={`flex-1 rounded-xl border px-4 py-3 font-mono font-bold text-lg transition-colors ${
                goal === v ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => changeGoal(-5)} data-testid="goal-minus" className="grid place-items-center w-10 h-10 rounded-sm border border-border hover:bg-secondary transition-colors">
            <Minus size={18} />
          </button>
          <span className="font-mono text-3xl font-bold tabular w-16 text-center" data-testid="goal-value">{goal}</span>
          <button onClick={() => changeGoal(5)} data-testid="goal-plus" className="grid place-items-center w-10 h-10 rounded-sm border border-border hover:bg-secondary transition-colors">
            <Plus size={18} />
          </button>
          <span className="text-xs text-muted-foreground">Ou ajustez précisément.</span>
        </div>
      </section>

      <section className="rounded-sm border border-border bg-card p-6">
        <h2 className="font-display font-bold text-lg mb-1">Sauvegarde de la progression</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {user
            ? `Connecté en tant que ${user.email}. Votre progression est automatiquement synchronisée sur votre compte et retrouvée sur tous vos appareils.`
            : "Votre progression est stockée dans ce navigateur. Connectez-vous (bouton en haut à droite) pour la synchroniser sur votre compte, ou exportez-la ci-dessous."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={doExport} data-testid="export-btn" className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3 font-semibold hover:-translate-y-px transition-transform">
            <Download size={18} /> Exporter
          </button>
          <button onClick={() => fileRef.current?.click()} data-testid="import-btn" className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm border border-border px-4 py-3 font-semibold hover:bg-secondary transition-colors">
            <Upload size={18} /> Importer
          </button>
          <input ref={fileRef} type="file" accept="application/json" onChange={doImport} className="hidden" data-testid="import-input" />
        </div>
      </section>

      <section className="rounded-sm border border-destructive/40 bg-card p-6">
        <h2 className="font-display font-bold text-lg mb-1 text-destructive">Zone sensible</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Réinitialiser efface toutes vos réponses, favoris et examens pour le niveau {META[level].label}.
        </p>
        <button onClick={doReset} data-testid="reset-btn" className="inline-flex items-center gap-2 rounded-sm border border-destructive/50 text-destructive px-4 py-3 font-semibold hover:bg-destructive/10 transition-colors">
          <Trash2 size={18} /> Réinitialiser ({META[level].label})
        </button>
      </section>
    </div>
  );
}
